<template>
  <div class="auth-page">
    <div class="container">
      <header>
        <h2 class="title">Введите номер телефона</h2>
      </header>

      <div test-id="phone-number" class="auth-page__field">
        <FormField
          v-model="authForm.phoneNumber"
          v-maska="'+7 ### ### ## ##'"
          prefix="+7 "
          :errors="v$.phoneNumber.$errors"
          @keydown.tab="v$.phoneNumber.$touch"
          class="auth-page__field__input"
        >
          <template #label>Номер телефона</template>
        </FormField>
      </div>

      <div
        v-if="clientId !== 'LoanApplicationLanding'"
        class="auth-page__checkboxes"
      >
        <div test-id="checkbox-agreement-bki" class="auth-page__checkbox">
          <BaseCheckbox v-model="authForm.checkbox1">
            Нажимая кнопку «Продолжить», Вы предоставляете АКБ «Алмазэргиэнбанк»
            АО
            <a
              href="https://albank.ru/upload/iblock/293/5yfyqr7dgrzxek9y8e656d5d1wz6a6r7.pdf"
              target="_blank"
              class="auth-page__checkbox__link"
              rel="noopener noreferrer"
            >
              <span>согласия и заверения</span></a
            >, в т.ч. на запрос в Бюро кредитных историй
          </BaseCheckbox>
        </div>

        <div test-id="checkbox-net-signing" class="auth-page__checkbox">
          <BaseCheckbox v-model="authForm.checkbox2">
            Также, нажимая кнопку «Продолжить», Вы принимаете
            <a
              href="digital_sign.html"
              target="_blank"
              class="auth-page__checkbox__link"
            >
              <span>Правила использования простой электронной подписи</span>
            </a>
          </BaseCheckbox>
        </div>
      </div>

      <div class="mt-40">
        <BaseButton
          test-id="button-continue"
          :disabled="!isAuthFormCompleted"
          @click.prevent="openSmsForm"
          >Продолжить</BaseButton
        >
      </div>
    </div>
  </div>

  <Teleport to="body">
    <Transition>
      <SmsModal
        v-if="isSmsFormOpen"
        :phone-number="clearPhoneNumber"
        :client-id="clientId"
        :redirect-uri="redirectUri"
        :code-challenge="codeChallenge"
        :is-auth-form-completed="isAuthFormCompleted"
        :error-code="errorCode"
        :error-message="errorMessage"
        :check-count="checkCount"
        :expires="expires"
        @on-close="onClose"
      />
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { onMounted, ref } from 'vue';

/* ---- Directive (prefix 'v' important) ---- */
import { maska as vMaska } from 'maska';

/* ---- Composables ---- */
import { useAuthForm } from '@/composables/useAuthForm';
import { useSmsFormToggle } from '@/composables/useSmsFormToggle';

/* ---- Components ---- */
import BaseButton from '@/components/BaseButton.vue';
import FormField from '@/components/FormField.vue';
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import SmsModal from '@/components/SmsModal.vue';

/* ---- State ---- */
const { clientId, authForm, isAuthFormCompleted, clearPhoneNumber, v$ } =
  useAuthForm();
const { isSmsFormOpen, openSmsForm, closeSmsForm } = useSmsFormToggle();

const route = useRoute();
const router = useRouter();

const redirectUri = ref();
const codeChallenge = ref();

const errorCode = ref();
const errorMessage = ref();
const checkCount = ref();
const expires = ref();

onMounted(() => {
  clientId.value = route.query.clientId;
  redirectUri.value = route.query.redirectUri;
  codeChallenge.value = route.query.codeChallenge;
  if (!clientId.value || !redirectUri.value) {
    router.push('/404');
  }

  errorCode.value = route.query.errorCode;
  if (errorCode.value) {
    let queryPhoneNumber = route.query.phoneNumber?.toString() || '';
    if (queryPhoneNumber.length > 10) {
      queryPhoneNumber = queryPhoneNumber.slice(1);
      authForm.phoneNumber = queryPhoneNumber;
    }
    errorMessage.value = route.query.errorMessage;

    checkCount.value = route.query.checkCount;
    expires.value = route.query.expires;

    openSmsForm();

    router.replace({
      ...router.currentRoute,
      query: {
        clientId: clientId.value,
        redirectUri: redirectUri.value,
        codeChallenge: codeChallenge.value,
      },
    });
  }
});

const onClose = () => {
  errorCode.value = '';
  errorMessage.value = '';
  checkCount.value = '';
  expires.value = '';
  closeSmsForm();
};
</script>

<style lang="scss" scoped>
.auth-page {
  padding-top: 12rem;
  max-width: 1040px;
  margin: 0 auto;
}

.auth-page > .container {
  max-width: 540px;
  background-color: $white;
  padding: 60px;
  border-radius: 24px;
  box-shadow: 0 6px 24px rgba($color: #27303e, $alpha: 6%);
}
.auth-page__field {
  width: 100%;
  margin: 4rem 0;
}

.auth-page__checkbox__link {
  color: $blue;
  text-decoration: none;
}

.auth-page__field__input,
.auth-page__alert {
  width: 100%;
}

.title {
  font-size: 28px;
  line-height: 32px;
}

@media only screen and (max-width: 768px) {
  /* For mobile phones: */
  .auth-page {
    padding-top: 0;
    max-width: 1040px;
    margin: 0 auto;
  }

  .auth-page > .container {
    width: 100%;
    height: 100%;
    background: none;
    padding: 40px 16px;
    border-radius: 0;
    box-shadow: none;
  }
}
</style>
